import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Carousel, Form, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { HomeData } from '../types';
import EventCard from './EventCard';
import ActivityCard from './ActivityCard';
import SkeletonLoader, { HeroSearchSkeleton, CarouselSkeleton, EventCardSkeleton, ActivityCardSkeleton, SuggestionItemSkeleton } from './common/SkeletonLoader';
import SEO from './common/SEO';
import './HomePage.scss';

const HomePage: React.FC = () => {
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ events: any[]; activities: any[] }>({ events: [], activities: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchHomeData = async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching home data...', forceRefresh ? '(force refresh)' : '');
      const data = await apiService.getHomeData(forceRefresh);
      console.log('Home data received:', data);
      setHomeData(data);
    } catch (err) {
      setError('Failed to load home page data');
      console.error('Error fetching home data:', err);
      console.error('Error details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeData();
  }, []);

  // Listen for country changes and refresh home data
  useEffect(() => {
    const handleCountryChange = (event: CustomEvent) => {
      console.log('Country changed, refreshing home data...', event.detail);
      // Clear home data cache and force refresh
      apiService.cache.clearCountryCache();
      // Add a small delay to ensure session is saved and cache is cleared
      setTimeout(() => {
        fetchHomeData(true);
      }, 100);
    };

    // Listen for custom country change event
    window.addEventListener('countryChanged', handleCountryChange as EventListener);
    
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange as EventListener);
    };
  }, []);

  // Fetch search suggestions with debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions({ events: [], activities: [] });
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const data = await apiService.getSearchSuggestions(searchQuery.trim());
        setSuggestions(data);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setSuggestions({ events: [], activities: [] });
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSuggestionClick = (type: 'event' | 'activity', slug: string) => {
    setShowSuggestions(false);
    if (type === 'event') {
      navigate(`/events/${slug}`);
    } else {
      navigate(`/activities/${slug}`);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.events.length > 0 || suggestions.activities.length > 0) {
      setShowSuggestions(true);
    }
  };

  if (error) {
    return (
      <div className="home-page">
        <section className="hero-search position-relative">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8}>
                <div className="search-wrapper text-center">
                  <div className="hero-title-wrapper mb-4">
                    <h1 className="display-3 fw-bold mb-3 hero-title">
                      <span className="hero-title-static">Live</span>{' '}
                      <span className="rotating-text">
                        <span className="rotating-word">Events</span>
                        <span className="rotating-word">Experience</span>
                        <span className="rotating-word">Activities</span>
                        <span className="rotating-word">Now!</span>
                      </span>
                    </h1>
                  </div>
                  <div className="search-box">
                    <Form onSubmit={handleSearch} className="d-flex">
                      <div className="search-input-wrapper flex-grow-1 position-relative">
                        <Form.Control
                          ref={searchInputRef}
                          type="text"
                          size="lg"
                          placeholder="Search Events, Activities, Experiences..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onFocus={handleInputFocus}
                          autoComplete="off"
                        />
                        {showSuggestions && (suggestions.events.length > 0 || suggestions.activities.length > 0) && (
                          <div ref={suggestionsRef} className="search-suggestions">
                            {loadingSuggestions ? (
                              <div className="suggestions-section">
                                <SuggestionItemSkeleton />
                                <SuggestionItemSkeleton />
                                <SuggestionItemSkeleton />
                              </div>
                            ) : (
                              <>
                                {suggestions.events.length > 0 && (
                                  <div className="suggestions-section">
                                    <div className="suggestions-header">
                                      <i className="fas fa-calendar-alt me-2"></i>
                                      Events
                                    </div>
                                    {suggestions.events.map((event) => (
                                      <div
                                        key={event.id}
                                        className="suggestion-item"
                                        onClick={() => handleSuggestionClick('event', event.slug)}
                                      >
                                        {event.thumbnail_url && (
                                          <img src={event.thumbnail_url} alt={event.title} className="suggestion-thumbnail" />
                                        )}
                                        <div className="suggestion-content">
                                          <div className="suggestion-title">{event.title}</div>
                                          {event.venue_name && (
                                            <div className="suggestion-meta">
                                              <i className="fas fa-map-marker-alt me-1"></i>
                                              {event.venue_name}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {suggestions.activities.length > 0 && (
                                  <div className="suggestions-section">
                                    <div className="suggestions-header">
                                      <i className="fas fa-map-marked-alt me-2"></i>
                                      Activities
                                    </div>
                                    {suggestions.activities.map((activity) => (
                                      <div
                                        key={activity.id}
                                        className="suggestion-item"
                                        onClick={() => handleSuggestionClick('activity', activity.slug)}
                                      >
                                        {activity.thumbnail_url && (
                                          <img src={activity.thumbnail_url} alt={activity.name} className="suggestion-thumbnail" />
                                        )}
                                        <div className="suggestion-content">
                                          <div className="suggestion-title">{activity.name}</div>
                                          {activity.location && (
                                            <div className="suggestion-meta">
                                              <i className="fas fa-map-marker-alt me-1"></i>
                                              {activity.location}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <Button type="submit" variant="primary" size="lg">
                        <i className="fas fa-search"></i>
                      </Button>
                    </Form>
                  </div>
                  <div className="popular-searches">
                    <Link to="/search?q=concerts" className="badge mx-1">Concerts</Link>
                    <Link to="/search?q=sports" className="badge mx-1">Sports</Link>
                    <Link to="/search?q=workshops" className="badge mx-1">Workshops</Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
        <div className="container my-5">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-5">
                  <div className="mb-4">
                    <i className="fas fa-exclamation-triangle fa-3x text-warning"></i>
                  </div>
                  <h4 className="mb-3">Unable to Load Content</h4>
                  <p className="text-muted mb-4">{error}</p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <Button 
                      variant="primary" 
                      onClick={() => fetchHomeData(true)}
                      className="px-4"
                    >
                      <i className="fas fa-redo me-2"></i>
                      Try Again
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => window.location.reload()}
                      className="px-4"
                    >
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh Page
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Generate structured data for home page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Quicket",
    "description": "Quicket is an event ticketing platform dedicated to simplifying event hosting for organizers and the ticket-buying experience for attendees.",
    "url": typeof window !== 'undefined' ? window.location.origin : 'https://quicket.me',
  };

  return (
    <div className="home-page">
      <SEO
        title="Quicket | Live Now!"
        description="Quicket is an event ticketing platform dedicated to simplifying event hosting for organizers and the ticket-buying experience for attendees."
        type="website"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="hero-search position-relative">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="search-wrapper text-center">
                <div className="hero-title-wrapper mb-4">
                  <h1 className="display-3 fw-bold mb-3 hero-title">
                    <span className="hero-title-static">Live</span>{' '}
                    <span className="rotating-text">
                      <span className="rotating-word">Events</span>
                      <span className="rotating-word">Experience</span>
                      <span className="rotating-word">Activities</span>
                      <span className="rotating-word">Now!</span>
                    </span>
                  </h1>
                </div>
                <div className="search-box">
                  <Form onSubmit={handleSearch} className="d-flex">
                    <div className="search-input-wrapper flex-grow-1 position-relative">
                      <Form.Control
                        ref={searchInputRef}
                        type="text"
                        size="lg"
                        placeholder="Search Events, Activities, Experiences..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={handleInputFocus}
                        autoComplete="off"
                      />
                      {showSuggestions && (suggestions.events.length > 0 || suggestions.activities.length > 0) && (
                        <div ref={suggestionsRef} className="search-suggestions">
                          {loadingSuggestions ? (
                            <div className="suggestions-section">
                              <SuggestionItemSkeleton />
                              <SuggestionItemSkeleton />
                              <SuggestionItemSkeleton />
                            </div>
                          ) : (
                            <>
                              {suggestions.events.length > 0 && (
                                <div className="suggestions-section">
                                  <div className="suggestions-header">
                                    <i className="fas fa-calendar-alt me-2"></i>
                                    Events
                                  </div>
                                  {suggestions.events.map((event) => (
                                    <div
                                      key={event.id}
                                      className="suggestion-item"
                                      onClick={() => handleSuggestionClick('event', event.slug)}
                                    >
                                      {event.thumbnail_url && (
                                        <img src={event.thumbnail_url} alt={event.title} className="suggestion-thumbnail" />
                                      )}
                                      <div className="suggestion-content">
                                        <div className="suggestion-title">{event.title}</div>
                                        {event.venue_name && (
                                          <div className="suggestion-meta">
                                            <i className="fas fa-map-marker-alt me-1"></i>
                                            {event.venue_name}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {suggestions.activities.length > 0 && (
                                <div className="suggestions-section">
                                  <div className="suggestions-header">
                                    <i className="fas fa-map-marked-alt me-2"></i>
                                    Activities
                                  </div>
                                  {suggestions.activities.map((activity) => (
                                    <div
                                      key={activity.id}
                                      className="suggestion-item"
                                      onClick={() => handleSuggestionClick('activity', activity.slug)}
                                    >
                                      {activity.thumbnail_url && (
                                        <img src={activity.thumbnail_url} alt={activity.name} className="suggestion-thumbnail" />
                                      )}
                                      <div className="suggestion-content">
                                        <div className="suggestion-title">{activity.name}</div>
                                        {activity.location && (
                                          <div className="suggestion-meta">
                                            <i className="fas fa-map-marker-alt me-1"></i>
                                            {activity.location}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <Button type="submit" variant="primary" size="lg">
                      <i className="fas fa-search"></i>
                    </Button>
                  </Form>
                </div>
                <div className="popular-searches">
                  <Link to="/search?q=concerts" className="badge mx-1">Concerts</Link>
                  <Link to="/search?q=sports" className="badge mx-1">Sports</Link>
                  <Link to="/search?q=workshops" className="badge mx-1">Workshops</Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Highlight Events Carousel */}
      {loading ? (
        <CarouselSkeleton />
      ) : homeData?.highlight_events && homeData.highlight_events.length > 0 ? (
        <section className="highlight-events">
          <Container>
            <h2 className="text-center mb-4 text-primary fw-bold">Featured Events</h2>
            <Carousel indicators={true} controls={true}>
              {homeData.highlight_events.map((event, index) => (
                <Carousel.Item key={event.id}>
                  <Link to={`/events/${event.slug}`} className="banner-wrapper carousel-clickable">
                    <picture>
                      <source media="(max-width: 1139px)" srcSet={event.thumbnail_url} />
                      <source media="(min-width: 1140px)" srcSet={event.banner_url} />
                      <img
                        src={event.banner_url}
                        alt={event.title}
                        loading="lazy"
                        className="w-100"
                        style={{ objectFit: 'cover' }}
                      />
                    </picture>
                    <div className="event-caption-overlay">
                      <div className="event-header carousel-event-header">
                        <div className="event-info">
                          <div className="event-date">
                            {new Date(event.starts).toLocaleDateString('en-US', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                          <h3 className="event-title">{event.title}</h3>
                          <div className="event-venue">
                            <i className="fas fa-map-marker-alt me-2"></i>{event.venue_name}
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </Link>
                </Carousel.Item>
              ))}
            </Carousel>
          </Container>
        </section>
      ) : null}

      {/* Events Section */}
      {loading ? (
        <section id="live-events">
          <div className="mt-2 mb-4 pt-2 pb-2 border-2 d-flex flex-column align-items-center">
            <SkeletonLoader type="text" width="150px" height="32px" className="mb-2" />
            <SkeletonLoader type="text" width="300px" height="18px" />
          </div>
          <Container>
            <Row className="justify-content-center">
              <Col md={10}>
                <Row className="justify-content-center align-items-end mb-3">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Col md={4} key={index} className="mb-3">
                      <EventCardSkeleton />
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Container>
        </section>
      ) : homeData?.events && homeData.events.length > 0 ? (
        <section id="live-events">
          <div className="mt-2 mb-4 pt-2 pb-2 border-2 d-flex flex-column align-items-center">
            <h1 className="text-primary">Events</h1>
            <p>Discover exciting events happening around you!</p>
          </div>
          <Container>
            <Row className="justify-content-center">
              <Col md={10}>
                <Row className="justify-content-center align-items-end mb-3">
                  {homeData.events.map((event) => (
                    <Col md={4} key={event.id} className="mb-3">
                      <EventCard event={event} />
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Container>
        </section>
      ) : null}

      {/* Activities Section */}
      {loading ? (
        <section className="pt-4 pb-4">
          <Container>
            <Row>
              <Col className="text-center">
                <div className="mt-2 mb-4 pt-2 pb-2 border-2">
                  <SkeletonLoader type="text" width="250px" height="32px" className="mx-auto mb-2" />
                  <SkeletonLoader type="text" width="400px" height="18px" className="mx-auto" />
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {Array.from({ length: 3 }).map((_, index) => (
                <Col md={4} key={index}>
                  <ActivityCardSkeleton />
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      ) : homeData?.activity_places && homeData.activity_places.length > 0 ? (
        <section className="pt-4 pb-4">
          <Container>
            <Row>
              <Col className="text-center">
                <div className="mt-2 mb-4 pt-2 pb-2 border-2">
                  <h1 className="text-primary">Explore Activities</h1>
                  <p>Discover activity places that you can book right away!</p>
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {homeData.activity_places.map((activity) => (
                <Col md={4} key={activity.id}>
                  <ActivityCard activity={activity} />
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      ) : null}

      {/* Host & Affiliate Section */}
      <section className="section q-bg host-affiliate-section" id="host-event">
        <Container>
          <Row className="justify-content-center align-items-stretch">
            <Col lg={6} className="mb-4 mb-lg-0 d-flex">
              <div className="feature-two-content host-affiliate-card text-center flex-fill p-4 h-100 d-flex flex-column justify-content-center">
                <span className="icon-box d-inline-block text-center rounded-circle mb-4">
                  <i className="text-white icon-lg sw-1 fas fa-calendar-alt"></i>
                </span>
                <h2>Host Your <span className="text-primary">Event!</span></h2>
                <p className="text-muted my-4">
                  Host your event with our modern and powerful interface. Take control of your event planning, promotion, and management, and deliver an unforgettable experience to your attendees.
                </p>
                <Link to="/host" className="btn btn-primary btn-lg mt-auto">
                  Get Started <span className="fs-22 ms-1 lh-1 align-middle icon">⇾</span>
                </Link>
              </div>
            </Col>
            <Col lg={6} className="d-flex">
              <div className="feature-two-content host-affiliate-card text-center flex-fill p-4 h-100 d-flex flex-column justify-content-center">
                <span className="icon-box d-inline-block text-center rounded-circle mb-4">
                  <i className="text-white icon-lg sw-1 fas fa-star"></i>
                </span>
                <h2>Join as <span className="text-primary">Affiliate</span></h2>
                <p className="text-muted my-4">
                  Earn commission by promoting events you love! Join our affiliate program and start earning on every ticket sale you help make. Track your performance with real-time analytics and enjoy fast payouts.
                </p>
                <Link to="/affiliate-program" className="btn btn-outline-primary btn-lg mt-auto">
                  Join as Affiliate <span className="fs-22 ms-1 lh-1 align-middle icon">★</span>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Ticket Management Section */}
      <section className="section q-bg ticket-management-section" id="ticket-management">
        <Container>
          <Row className="justify-content-center">
            <Col lg={12} xl={12}>
              <div className="ticket-management-wrapper">
                <div className="text-center mb-5">
                  <span className="icon-box d-inline-block text-center rounded-circle mb-4">
                    <i className="text-white icon-lg sw-1 fas fa-sync-alt"></i>
                  </span>
                  <h2 className="mb-3">Manage Your <span className="text-primary">Tickets!</span></h2>
                  <p className="lead text-muted mb-0">
                    Need to transfer your ticket to someone else? Our secure transfer system makes it easy to pass your tickets to friends or family. Quick, safe, and hassle-free!
                  </p>
                </div>
                
                <div className="ticket-features-grid">
                  <Link to="/transfer" className="ticket-feature-card">
                    <div className="feature-icon">
                      <i className="fas fa-exchange-alt"></i>
                    </div>
                    <h4>Transfer Tickets</h4>
                    <p>Easily transfer your tickets to friends or family members with our secure transfer system.</p>
                    <span className="feature-link">
                      Transfer Now <i className="fas fa-arrow-right ms-2"></i>
                    </span>
                  </Link>
                  
                  <Link to="/verify" className="ticket-feature-card">
                    <div className="feature-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <h4>Verify Pass</h4>
                    <p>Verify your ticket authenticity and view all pass details using your identifier and email.</p>
                    <span className="feature-link">
                      Verify Now <i className="fas fa-arrow-right ms-2"></i>
                    </span>
                  </Link>
                  
                  <Link to="/secure-pass" className="ticket-feature-card">
                    <div className="feature-icon">
                      <i className="fas fa-shield-alt"></i>
                    </div>
                    <h4>Secure Pass Access</h4>
                    <p>Access your passes securely using OTP verification via email or phone number.</p>
                    <span className="feature-link">
                      Access Now <i className="fas fa-arrow-right ms-2"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
